import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  ColorsV2,
  getToken,
  Spacer,
  Spinner,
  spaces,
} from '@electricjs/arc';

import { PackageNameEnum } from '@electricjs/core_entity-client';
import { getFeatureDisplayNameMap, FEATURES_BY_PLAN } from './constants';
import { FeatureKey, PackageFeatures } from './types';

import { useListAllPackagesQuery } from '@/redux/slices/packageApiSlice';

const LicenseTile = styled(Flex)`
  flex-direction: column;
  align-items: center;
  min-width: 30rem;
  padding: 20px;
  background-color: ${getToken(ColorsV2.BACKGROUND_ALT)};
  border: 2px solid ${getToken(ColorsV2.BORDER_LIGHT)};
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    border-color: ${getToken(ColorsV2.PRIMARY)};

    &[aria-disabled='true'] {
      cursor: not-allowed;
      border-color: ${getToken(ColorsV2.BORDER_LIGHT)};
    }
  }

  &[aria-selected='true'] {
    background-color: ${getToken(ColorsV2.PRIMARY_LIGHTEST)};
    border-color: ${getToken(ColorsV2.PRIMARY)};
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
`;

const LicenseCountDisplay = styled(Box)`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  border-radius: 0.4rem;
  padding: 0.5rem 1rem;
  display: inline-block;
`;

type LicenseTileOptionProps = {
  packageId: string;
  packageSlug: typeof PackageNameEnum.Pro | typeof PackageNameEnum.Lyte;
  selected: boolean;
  licenseCount: number | null;
  licensesAvailable: number | null;
  onSelect: (packageId: string) => void;
  hideLicenseCount?: boolean;
  disabled?: boolean;
  onAddLicenses?: () => void;
};

const LicenseTileOption = ({
  packageId,
  packageSlug,
  selected,
  licensesAvailable,
  onSelect,
  hideLicenseCount,
  disabled,
  onAddLicenses,
}: LicenseTileOptionProps) => {
  const { t } = useTranslation('common');
  const features = Object.keys(PackageFeatures) as FeatureKey[];

  const { space_xs, space_s, space_m } = spaces;

  const { data: packages, isLoading: isLoadingPackages } =
    useListAllPackagesQuery();

  const packageDisplayName = packages?.find(
    pkg => pkg.name === packageSlug
  )?.displayName;

  const currentPackageFeatures: Partial<FeatureKey>[] =
    FEATURES_BY_PLAN[packageSlug];

  const featureDisplayNameMap = getFeatureDisplayNameMap(t);

  const iconName =
    packageSlug === PackageNameEnum.Pro ? 'shield-check' : 'lightbulb-gear';
  const packageIconIntent = (() => {
    if (disabled) return 'disabled';
    if (selected) return 'primary';
    return 'neutral';
  })();
  const textIntent = (() => {
    if (disabled) return 'disabled';
    if (selected) return 'brand';
    return 'primary';
  })();
  const getIconIntent = (included: boolean, disabled: boolean | undefined) => {
    if (disabled) return 'disabled';
    if (included) return 'success';
    return 'error';
  };

  const handleClick = () => {
    if (disabled || !packageId) return;

    onSelect(packageId);
  };

  const licenseCountDisplay = (() => {
    if (licensesAvailable === null) {
      return t('onboardings.licenseSelection.unlimitedLicenses', {
        displayName: packageDisplayName,
      });
    }

    return t('onboardings.licenseSelection.licensesAvailable', {
      count: licensesAvailable,
      displayName: packageDisplayName,
    });
  })();

  if (isLoadingPackages) {
    return <Spinner />;
  }

  return (
    <Flex vertical alignItems="center" width="fit-content">
      {!hideLicenseCount && (
        <Flex
          alignItems="center"
          columnGap={spaces.space_s_rem}
          mb={spaces.space_m_rem}>
          <LicenseCountDisplay>
            <Text>{licenseCountDisplay}</Text>
          </LicenseCountDisplay>
          {disabled && onAddLicenses && (
            <Button
              id={`add-licenses-button-${packageSlug}`}
              variant="outline"
              onClick={e => {
                e.stopPropagation();
                onAddLicenses();
              }}>
              {t('onboardings.licenseSelection.addLicensesButton')}
            </Button>
          )}
        </Flex>
      )}
      <LicenseTile
        onClick={handleClick}
        aria-selected={selected}
        aria-disabled={disabled}
        testId={`license-tile-${packageSlug}`}>
        <Flex vertical alignItems="center">
          <Icon
            icon={iconName}
            customSize="7.2rem"
            intent={packageIconIntent}
          />
          <Spacer y={space_s} />
          <Text variant="heading-3" intent={textIntent}>
            {packageDisplayName}
          </Text>
        </Flex>
        <Spacer y={space_m} />
        <FeatureList>
          {features.map((feature, index) => {
            const included = currentPackageFeatures.includes(feature);
            const iconIntent = getIconIntent(included, disabled);

            return (
              <>
                <FeatureItem key={feature}>
                  <Icon
                    icon={included ? 'check' : 'close'}
                    size="small"
                    intent={iconIntent}
                  />
                  <Spacer x={space_xs} />
                  <Text variant="body-2" intent={textIntent}>
                    {featureDisplayNameMap[feature as FeatureKey]}
                  </Text>
                </FeatureItem>
                {index < features.length - 1 && <Spacer y={space_xs} />}
              </>
            );
          })}
        </FeatureList>
      </LicenseTile>
    </Flex>
  );
};

export default LicenseTileOption;
